import { ActionConfirmation } from '@app/components/base/ActionConfirmation';
import { NullMessageMui } from '@mi-front/ui';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Control,
  FieldErrors,
  FieldPath,
  FieldValues,
  useFieldArray,
  useFormContext,
} from 'react-hook-form-mui';
import { useIntl } from 'react-intl';

export interface FormArrayFieldMuiProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TFieldName;
  title?: string;
  emptyMessage?: string;
  addButtonLabel?: string;
  defaultItem: any;
  renderItem: (props: { index: number; onRemove: () => void }) => React.ReactNode;
  deleteConfirmation?: {
    title: string;
    description: string | ((item: any, index: number) => string);
    confirmText?: string;
    cancelText?: string;
  };
  getItemDisplayName?: (item: any, index: number) => string;
}

export function FormArrayItem<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  title,
  emptyMessage,
  addButtonLabel,
  defaultItem,
  renderItem,
  deleteConfirmation,
  getItemDisplayName,
}: FormArrayFieldMuiProps<TFieldValues, TFieldName>) {
  const { $t } = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext<TFieldValues>();
  const { fields, append, remove } = useFieldArray({
    control: control as Control<TFieldValues>,
    name: name as any,
  });

  const fieldErrors = errors[name as string] as FieldErrors<TFieldValues> | undefined;

  const resolvedEmptyMessage =
    emptyMessage ??
    $t({
      id: 'form.array.emptyMessage',
      defaultMessage: 'No hay items definidos aún',
    });
  const resolvedAddButtonLabel =
    addButtonLabel ??
    $t({
      id: 'form.array.addButtonLabel',
      defaultMessage: 'Agregar',
    });

  const handleAdd = () => {
    append(defaultItem);
  };

  const handleRemove = (index: number) => {
    remove(index);
  };

  const renderDeleteButton = (index: number, item: any) => {
    const onRemove = () => handleRemove(index);

    if (deleteConfirmation) {
      const description =
        typeof deleteConfirmation.description === 'function'
          ? deleteConfirmation.description(item, index)
          : deleteConfirmation.description;

      const itemName = getItemDisplayName ? getItemDisplayName(item, index) : undefined;

      const finalDescription = itemName ? description.replace('{name}', itemName) : description;

      return (
        <ActionConfirmation
          trigger={(onClick: (event: React.MouseEvent<HTMLElement>) => void) => (
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onClick(e);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
          title={deleteConfirmation.title}
          description={finalDescription}
          onConfirm={onRemove}
          confirmText={
            deleteConfirmation.confirmText ??
            $t({ id: 'common.delete', defaultMessage: 'Eliminar' })
          }
          cancelText={
            deleteConfirmation.cancelText ?? $t({ id: 'common.cancel', defaultMessage: 'Cancelar' })
          }
          icon="warning"
          iconColor="#ff7d55"
          placement="bottom-start"
        />
      );
    }

    return (
      <IconButton size="small" onClick={onRemove}>
        <DeleteIcon />
      </IconButton>
    );
  };

  return (
    <Stack spacing={3}>
      {fields.length === 0 ? (
        <NullMessageMui>{resolvedEmptyMessage}</NullMessageMui>
      ) : (
        <Stack spacing={2}>
          {fields.map((field, index) => {
            const item = (field as any) || {};
            return (
              <Card key={field.id}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <Box flexGrow={1}>
                      {renderItem({
                        index,
                        onRemove: () => handleRemove(index),
                      })}
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>{renderDeleteButton(index, item)}</Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {(title || resolvedAddButtonLabel) && (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {title && <Typography variant="subtitle1">{title}</Typography>}
          <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" size="small">
            {resolvedAddButtonLabel}
          </Button>
        </Stack>
      )}

      {fieldErrors?.root && (
        <Alert severity="error">
          {(fieldErrors.root as any)?.message ??
            $t({
              id: 'form.array.fieldError',
              defaultMessage: 'Error en el campo',
            })}
        </Alert>
      )}
    </Stack>
  );
}
